import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Switch from '@mui/joy/Switch';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Grid from '@mui/joy/Grid';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Snackbar from '@mui/joy/Snackbar';
import Alert from '@mui/joy/Alert';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import { getSettings, setSettings } from '../../utils/settings';

// Icons
import NotificationsIcon from '@mui/icons-material/Notifications';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import DesktopIcon from '@mui/icons-material/DesktopWindows';

export default function NotificationPreferencesPanel() {
  const [loading, setLoading] = useState(true);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [settings, setSettingsState] = useState({
    notifications: {
      enabled: true,
      channels: {
        inApp: true,
        email: false,
        sms: false
      },
      preferences: {
        lowStock: true,
        orderProcessing: true,
        systemAlerts: true,
        dailySummary: false,
        weeklyReport: false
      },
      thresholds: {
        lowStockThreshold: 10,
        criticalStockThreshold: 5
      },
      email: {
        address: '',
        verified: false
      },
      sms: {
        number: '',
        verified: false
      }
    }
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const showSnackbar = (msg, severity = 'success') => {
    setSnackbar({
      open: true,
      message: msg,
      severity
    });
  };

  // Load settings on component mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await getSettings();
        
        if (data.notifications) {
          const newSettings = {
            ...settings,
            notifications: {
              ...settings.notifications,
              ...data.notifications
            }
          };
          setSettingsState(newSettings);
          setOriginalSettings(JSON.parse(JSON.stringify(newSettings))); // Deep copy
        } else {
          setOriginalSettings(JSON.parse(JSON.stringify(settings))); // Deep copy of defaults
        }
        
      } catch (error) {
        console.error('Error loading notification settings:', error);
        showSnackbar('Failed to load settings', 'danger');
        setOriginalSettings(JSON.parse(JSON.stringify(settings))); // Deep copy of defaults
      } finally {
        setLoading(false);
      }
    }
    
    loadSettings();
  }, []);

  // Save settings
  const handleSave = async () => {
    try {
      setLoading(true);
      const allSettings = await getSettings();
      
      await setSettings({
        ...allSettings,
        notifications: settings.notifications
      });
      
      showSnackbar('Notification settings saved successfully');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showSnackbar('Failed to save settings', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Clear changes - reset to original loaded settings
  const handleClearChanges = () => {
    if (originalSettings) {
      setSettingsState(JSON.parse(JSON.stringify(originalSettings))); // Deep copy
      showSnackbar('Changes cleared successfully', 'warning');
    }
  };

  // Handle nested setting changes
  const handleSettingChange = (path, value) => {
    setSettingsState(prev => {
      const newSettings = { ...prev };
      const pathParts = path.split('.');
      let current = newSettings;
      
      // Navigate to the right level
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) current[pathParts[i]] = {};
        current = current[pathParts[i]];
      }
      
      // Set the value
      current[pathParts[pathParts.length - 1]] = value;
      return newSettings;
    });
  };

  const notificationTypes = [
    {
      key: 'lowStock',
      label: 'Low Stock Alerts',
      description: 'Get notified when inventory items fall below the reorder threshold',
      icon: <WarningIcon />,
      color: '#ff9800'
    },
    {
      key: 'orderProcessing',
      label: 'Order Processing',
      description: 'Notifications about order status changes and processing updates',
      icon: <ShoppingCartIcon />,
      color: '#2196f3'
    },
    {
      key: 'systemAlerts',
      label: 'System Alerts',
      description: 'Important system notifications and error alerts',
      icon: <InfoIcon />,
      color: '#f44336'
    },
    {
      key: 'dailySummary',
      label: 'Daily Summary',
      description: 'Daily inventory and order summary reports',
      icon: <InventoryIcon />,
      color: '#4caf50'
    },
    {
      key: 'weeklyReport',
      label: 'Weekly Report',
      description: 'Weekly performance and inventory analytics',
      icon: <InventoryIcon />,
      color: '#9c27b0'
    }
  ];

  const channels = [
    {
      key: 'inApp',
      label: 'In-App Notifications',
      description: 'Show notifications within the application',
      icon: <DesktopIcon />,
      color: '#2196f3'
    },
    {
      key: 'email',
      label: 'Email Notifications',
      description: 'Send notifications to your email address',
      icon: <EmailIcon />,
      color: '#ff9800'
    },
    {
      key: 'sms',
      label: 'SMS Notifications',
      description: 'Send notifications via text message',
      icon: <SmsIcon />,
      color: '#4caf50'
    }
  ];

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography level="h3" sx={{ color: 'white', mb: 1 }}>
          Notification Preferences
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
          About Notifications
        </Typography>
      </Box>
        
      {/* Main Toggle */}
      <Box sx={{ mb: 4 }}>
        <Card 
          variant="soft"
          sx={{ 
            bgcolor: settings.notifications.enabled ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
            border: settings.notifications.enabled ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(158, 158, 158, 0.3)',
            borderRadius: 'lg'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <NotificationsIcon sx={{ 
                color: settings.notifications.enabled ? '#4caf50' : '#9e9e9e',
                fontSize: 32
              }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography level="title-lg" sx={{ fontWeight: 'bold', color: 'white' }}>
                  Notifications
            </Typography>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                  {settings.notifications.enabled ? 'Notifications are enabled' : 'Notifications are disabled'}
            </Typography>
              </Box>
              <Switch
                checked={settings.notifications.enabled}
                onChange={(e) => handleSettingChange('notifications.enabled', e.target.checked)}
                size="lg"
                                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: settings.notifications.enabled ? '#4caf50' : '#666666',
                      opacity: 1
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#ffffff'
                    }
                  }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      
      {settings.notifications.enabled && (
        <>
          {/* Notification Channels */}
      <Box sx={{ mb: 4 }}>
            <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
              Notification Channels
            </Typography>
            
            <Grid container spacing={2}>
              {channels.map(channel => (
                <Grid xs={12} md={4} key={channel.key}>
                  <Card 
                    variant="soft"
          sx={{ 
                      bgcolor: `rgba(${parseInt(channel.color.slice(1, 3), 16)}, ${parseInt(channel.color.slice(3, 5), 16)}, ${parseInt(channel.color.slice(5, 7), 16)}, 0.1)`,
                      border: `1px solid rgba(${parseInt(channel.color.slice(1, 3), 16)}, ${parseInt(channel.color.slice(3, 5), 16)}, ${parseInt(channel.color.slice(5, 7), 16)}, 0.3)`,
                      borderRadius: 'lg',
                      height: '100%'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ color: channel.color, mt: 0.5 }}>
                          {channel.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography level="title-sm" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                            {channel.label}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            {channel.description}
                          </Typography>
                        </Box>
                        <Switch
                          checked={settings.notifications.channels[channel.key]}
                          onChange={(e) => handleSettingChange(`notifications.channels.${channel.key}`, e.target.checked)}
                          size="sm"
                          sx={{
                            '& .MuiSwitch-track': {
                              backgroundColor: settings.notifications.channels[channel.key] ? '#4caf50' : '#666666',
                              opacity: 1
                            },
                            '& .MuiSwitch-thumb': {
                              backgroundColor: '#ffffff'
                            }
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Notification Types */}
          <Box sx={{ mb: 4 }}>
            <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
              Notification Types
        </Typography>
        
            <Grid container spacing={2}>
              {notificationTypes.map(type => (
                <Grid xs={12} md={6} key={type.key}>
        <Card 
                    variant="soft"
          sx={{ 
                      bgcolor: `rgba(${parseInt(type.color.slice(1, 3), 16)}, ${parseInt(type.color.slice(3, 5), 16)}, ${parseInt(type.color.slice(5, 7), 16)}, 0.1)`,
                      border: `1px solid rgba(${parseInt(type.color.slice(1, 3), 16)}, ${parseInt(type.color.slice(3, 5), 16)}, ${parseInt(type.color.slice(5, 7), 16)}, 0.3)`,
                      borderRadius: 'lg',
                      height: '100%'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ color: type.color, mt: 0.5 }}>
                          {type.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography level="title-sm" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                            {type.label}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            {type.description}
                          </Typography>
                        </Box>
                        <Switch
                          checked={settings.notifications.preferences[type.key]}
                          onChange={(e) => handleSettingChange(`notifications.preferences.${type.key}`, e.target.checked)}
                          size="sm"
                          sx={{
                            '& .MuiSwitch-track': {
                              backgroundColor: settings.notifications.preferences[type.key] ? '#4caf50' : '#666666',
                              opacity: 1
                            },
                            '& .MuiSwitch-thumb': {
                              backgroundColor: '#ffffff'
                            }
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Thresholds */}
          <Box sx={{ mb: 4 }}>
            <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
              Alert Thresholds
            </Typography>
            
            <Card variant="soft" sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)', borderRadius: 'lg' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel sx={{ color: 'white' }}>Low Stock Threshold</FormLabel>
                      <Input
                        type="number"
                        value={settings.notifications.thresholds.lowStockThreshold}
                        onChange={(e) => handleSettingChange('notifications.thresholds.lowStockThreshold', parseInt(e.target.value) || 0)}
                        slotProps={{
                          input: { min: 0 }
                        }}
                        sx={{ borderRadius: 'md' }}
                      />
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Get notified when stock falls below this quantity
                        </Typography>
                    </FormControl>
              </Grid>
              
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel sx={{ color: 'white' }}>Critical Stock Threshold</FormLabel>
                      <Input
                        type="number"
                        value={settings.notifications.thresholds.criticalStockThreshold}
                        onChange={(e) => handleSettingChange('notifications.thresholds.criticalStockThreshold', parseInt(e.target.value) || 0)}
                        slotProps={{
                          input: { min: 0 }
                        }}
                        sx={{ borderRadius: 'md' }}
                      />
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Get urgent notifications when stock falls below this quantity
                        </Typography>
                    </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      
          {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
            <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
              Contact Information
          </Typography>
          
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Card variant="soft" sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)', borderRadius: 'lg' }}>
            <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <EmailIcon sx={{ color: '#ff9800' }} />
                      <Typography level="title-md" sx={{ fontWeight: 'bold', color: 'white' }}>
                        Email Address
                      </Typography>
                      {settings.notifications.email.verified && (
                        <Chip variant="soft" color="success" size="sm">
                          Verified
                        </Chip>
                      )}
                    </Stack>
                    
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={settings.notifications.email.address}
                        onChange={(e) => handleSettingChange('notifications.email.address', e.target.value)}
                        disabled={!settings.notifications.channels.email}
                        sx={{ borderRadius: 'md' }}
                      />
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Email notifications will be sent to this address
                      </Typography>
              </FormControl>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid xs={12} md={6}>
                <Card variant="soft" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: 'lg' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <SmsIcon sx={{ color: '#4caf50' }} />
                      <Typography level="title-md" sx={{ fontWeight: 'bold', color: 'white' }}>
                        Phone Number
                      </Typography>
                      {settings.notifications.sms.verified && (
                        <Chip variant="soft" color="success" size="sm">
                          Verified
                        </Chip>
                      )}
                    </Stack>
                    
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="Enter your phone number"
                        value={settings.notifications.sms.number}
                        onChange={(e) => handleSettingChange('notifications.sms.number', e.target.value)}
                        disabled={!settings.notifications.channels.sms}
                        sx={{ borderRadius: 'md' }}
                      />
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        SMS notifications will be sent to this number
              </Typography>
                    </FormControl>
            </CardContent>
          </Card>
              </Grid>
            </Grid>
        </Box>
        </>
      )}
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          onClick={handleClearChanges}
          disabled={!originalSettings || JSON.stringify(settings) === JSON.stringify(originalSettings)}
          sx={{
            px: 3,
            py: 1.5,
            borderColor: '#ff9800',
            color: '#ff9800',
            borderRadius: 'md',
            '&:hover': {
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              borderColor: '#ff9800'
            }
          }}
        >
          Clear Changes
        </Button>
        <Button
          variant="solid"
          startDecorator={<SaveIcon />}
          onClick={handleSave}
          disabled={loading}
          sx={{
            px: 4,
            py: 1.5,
            bgcolor: '#4caf50',
            borderRadius: 'md',
            '&:hover': {
              bgcolor: '#388e3c'
            }
          }}
        >
          Save Notification Settings
        </Button>
      </Box>
      
      {/* Help Info */}
      <Sheet 
        variant="soft"
        sx={{
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 'lg',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography level="title-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold', mb: 1 }}>
          About Notifications
        </Typography>
        <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Configure how and when you receive notifications about your inventory. In-app notifications appear within the application, while email and SMS notifications are sent to your registered contact information.
        </Typography>
      </Sheet>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        color={snackbar.severity}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          color={snackbar.severity}
          variant="solid"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
